/* eslint-disable react/no-array-index-key */
import {
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  styled,
} from '@mui/material'
import { grey } from '@mui/material/colors'
import FullHeightFlexContainer from 'components/ui/FullHeightFlexContainer'
import { DocumentGeneral } from 'entities/DocumentsGeneral'
import { useGeneralOcrTableContext } from 'hooks/useGeneralOcrTableRefContext'
import { useHighlightContext } from 'hooks/useHighlightContext'
import { useTableContext } from 'hooks/useTabContext'
import { useState, useRef, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'

const DISABLED_COLOR = grey[500]

const MyTable = styled(Table)(({ theme }) => ({
  height: '1px', // trick 100% height td
  '& .MuiTableCell-root': {
    borderRight: '1px solid rgba(224, 224, 224, 1)',
  },
  '& .MuiTableCell-body': {
    whiteSpace: 'pre-line',
    padding: 0,
    verticalAlign: 'top',
    '&:hover': {
      background: grey[100],
    },
    '&:active': {
      background: grey[100],
    },
    '& div': {
      position: 'relative',
      height: '100%',
    },
    '& div div': {
      padding: '0.8rem',
    },
    '& textarea': {
      display: 'none',
      padding: '0.6rem',
      position: 'absolute',
      left: 0,
      top: 0,
      width: '100%',
      height: '100%',
      resize: 'none',
      overflow: 'hidden',
    },
    '& textarea:focus': {
      outlineColor: theme.palette.primary.main,
    },
  },
}))

interface Props {
  docDetail: DocumentGeneral
  disabled: boolean
}

function makeHLName(tIdx: number, rIdx: number, cIdx: number): string {
  return `cell.${tIdx}.${rIdx}.${cIdx}`
}

function Form({ docDetail, disabled }: Props) {
  const { update: updateHL } = useHighlightContext()
  // used to force re-render
  const [dummy, setDummy] = useState<boolean>(false)
  const { register, setValue } = useFormContext()
  const tables = docDetail.tablesByRowCol
  const inputsRef = useRef<HTMLTextAreaElement[][]>(
    Array(tables.length)
      .fill(null)
      .map(() => Array(0)),
  )
  const isFocused = useRef(false)
  const undoValue = useRef('')
  const maxCols = docDetail.maxCols
  const generalOcrTableRef = useGeneralOcrTableContext()
  const { currentTableIndex, handleTableChange } = useTableContext()

  const fieldHandlers = useMemo(
    () => ({
      onMouseOver: (tIdx: number, rIdx: number, cIdx: number) => {
        !isFocused.current && updateHL(makeHLName(tIdx, rIdx, cIdx), false)
      },
      onMouseOut: () => {
        !isFocused.current && updateHL(null)
      },
    }),
    [updateHL],
  )

  const fieldInputFocusHandlers = useMemo(
    () => ({
      onFocus: (e, tIdx: number, rIdx: number, cIdx: number) => {
        updateHL(makeHLName(tIdx, rIdx, cIdx))
        const end = e.target.value.length
        e.target.setSelectionRange(end, end)
        isFocused.current = true
        undoValue.current = e.target.value
      },
      onBlur: (e) => {
        e.target.style.display = 'none'
        isFocused.current = false
        updateHL(null)
      },
    }),
    [updateHL],
  )

  const fieldInputHandlers = useMemo(
    () => ({
      // CONSIDER: c: cell type?
      onChange: (e, c) => {
        c.editedText = e.target.value
        setDummy(!dummy) // re-render
        // don't delete this.
        // _a is used to force form to be dirty when any of table cells are edited
        setValue('_a', '', { shouldDirty: true })
      },
      onKeyDown: (e, c, tIdx: number, cIdx: number) => {
        if (e.key === 'Escape') {
          c.editedText = undoValue.current
          const cInput = inputsRef.current[tIdx][cIdx]
          cInput.value = undoValue.current
          cInput.blur()
          setDummy(!dummy) // re-render
          return
        }
        if (e.key === 'Tab') {
          if (e.shiftKey) {
            if (c.cIdx! > 0) {
              const nInput = inputsRef.current[tIdx][cIdx - 1]
              nInput.style.display = 'initial'
            }
          } else {
            if (inputsRef.current[tIdx].length > cIdx + 1) {
              const nInput = inputsRef.current[tIdx][cIdx + 1]
              nInput.style.display = 'initial'
            }
          }
        }
      },
    }),
    [setDummy, dummy, setValue],
  )

  const fieldInputWrapperOnClick = useMemo(
    () => (e) => {
      const input = e.target.nextElementSibling
      input.style.display = 'initial'
      input.focus()
    },
    [],
  )

  if (tables.length === 0) {
    return <div style={{ padding: '5px' }}>表がありません</div>
  }

  const setTableRefs = (el, tableIndex, rowIndex, columnIndex) => {
    if (!el || !generalOcrTableRef.current) {
      return
    }
    if (!generalOcrTableRef.current?.[tableIndex]) {
      generalOcrTableRef.current[tableIndex] = []
    }
    if (!generalOcrTableRef.current?.[tableIndex][rowIndex]) {
      generalOcrTableRef.current[tableIndex][rowIndex] = []
    }
    generalOcrTableRef.current[tableIndex][rowIndex][columnIndex] = el!
  }

  return (
    <>
      <FullHeightFlexContainer>
        <Box sx={{ px: 2, py: 1 }}>
          <ToggleButtonGroup
            value={currentTableIndex}
            exclusive
            onChange={(_e, newIdx) =>
              handleTableChange(newIdx === null ? currentTableIndex : newIdx)
            }
            data-testid="general-type-table-toggle"
          >
            <ToggleButton value="" disabled>
              <span style={{ color: 'black' }}>tables:</span>
            </ToggleButton>
            {tables.map((_t, idx) => (
              <ToggleButton key={idx} value={idx}>
                {idx + 1}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        {tables.map((t, idx) => (
          <TableContainer
            key={idx}
            hidden={currentTableIndex !== idx}
            data-testid={`general-type-table${idx}`}
          >
            <MyTable
              stickyHeader
              size={'medium'}
              aria-label="base fields table"
              id={`general-t-${idx}`}
            >
              <TableHead data-testid={`general-type-table-head${idx}`}>
                <TableRow>
                  {[...Array(maxCols[idx]).keys()].map((i) => (
                    <TableCell key={i}>列{i + 1}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody data-testid={`general-type-table-body${idx}`}>
                {t.map((r, i) => (
                  <TableRow key={i}>
                    {r.map((c, j) => (
                      <TableCell
                        key={`${idx}-${i}-${j}`}
                        colSpan={c.columnSpan}
                        rowSpan={c.rowSpan}
                        onMouseOver={() =>
                          fieldHandlers.onMouseOver(idx, c.rowIndex, c.columnIndex)
                        }
                        onMouseOut={fieldHandlers.onMouseOut}
                      >
                        <div>
                          <div
                            key={makeHLName(idx, c.rowIndex, c.columnIndex)}
                            ref={(el) => {
                              setTableRefs(el, idx, c.rowIndex, c.columnIndex)
                            }}
                            onClick={(e) => !disabled && fieldInputWrapperOnClick(e)}
                            style={disabled ? { color: DISABLED_COLOR } : undefined}
                          >
                            {c.editedText}
                          </div>
                          <textarea
                            ref={(el) => (inputsRef.current[idx][c.cIdx!] = el!)}
                            onFocus={(e) =>
                              fieldInputFocusHandlers.onFocus(e, idx, c.rowIndex, c.columnIndex)
                            }
                            onChange={(e) => fieldInputHandlers.onChange(e, c)}
                            onBlur={fieldInputFocusHandlers.onBlur}
                            onKeyDown={(e) => fieldInputHandlers.onKeyDown(e, c, idx, c.cIdx!)}
                            defaultValue={c.editedText}
                          />
                        </div>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </MyTable>
          </TableContainer>
        ))}
      </FullHeightFlexContainer>
      {/* this input makes form dirty that browser back shows confirm msg */}
      <input type="hidden" {...(register('_a') as any)} />
    </>
  )
}

export default Form
